import { useState, useEffect } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";

interface VideoLightboxProps {
  videoUrl: string | null;
  onClose: () => void;
}

export const VideoLightbox = ({ videoUrl, onClose }: VideoLightboxProps) => {
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    
    if (videoUrl) {
      document.addEventListener("keydown", handleEscape);
      document.body.style.overflow = "hidden";
    }
    
    return () => {
      document.removeEventListener("keydown", handleEscape);
      document.body.style.overflow = "";
    };
  }, [videoUrl, onClose]);

  return (
    <AnimatePresence>
      {videoUrl && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-foreground/90 backdrop-blur-sm"
          onClick={onClose}
        >
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 rounded-full bg-background/20 hover:bg-background/30 text-background transition-colors z-10"
            aria-label="Close video"
          >
            <X className="h-6 w-6" />
          </button>

          {/* Video Container */}
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="relative w-full max-w-sm max-h-[85vh] aspect-[9/16]"
            onClick={(e) => e.stopPropagation()}
          >
            <video
              src={videoUrl}
              className="w-full h-full object-contain rounded-2xl"
              controls
              autoPlay
              playsInline
            />
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
